// frontend/src/pages/company/DeliveryZones.jsx
// Хүргэлтийн бүсчлэл удирдах

import React, { useState, useEffect } from 'react';
import CompanyLayout from '../../components/layout/CompanyLayout';
import zoneService from '../../services/zoneService';
import { districtAPI } from '../../services/api';

const DeliveryZones = () => {
  const [loading, setLoading] = useState(true);
  const [zones, setZones] = useState([]);
  const [districts, setDistricts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingZone, setEditingZone] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    zone_name: '',
    default_fee: '',
    description: '',
    color: '#3B82F6',
    district_ids: []
  });

  const [errors, setErrors] = useState({});

  // Predefined colors
  const colorOptions = [
    { value: '#3B82F6', label: 'Цэнхэр', bg: 'bg-blue-500' },
    { value: '#10B981', label: 'Ногоон', bg: 'bg-green-500' },
    { value: '#F59E0B', label: 'Шар', bg: 'bg-yellow-500' },
    { value: '#EF4444', label: 'Улаан', bg: 'bg-red-500' },
    { value: '#8B5CF6', label: 'Нил ягаан', bg: 'bg-purple-500' },
    { value: '#EC4899', label: 'Ягаан', bg: 'bg-pink-500' },
    { value: '#6366F1', label: 'Индиго', bg: 'bg-indigo-500' },
    { value: '#14B8A6', label: 'Шилжмэл ногоон', bg: 'bg-teal-500' }
  ];

  useEffect(() => {
    fetchZones();
    fetchDistricts();
  }, []);

  const fetchZones = async () => {
    try {
      setLoading(true);
      const response = await zoneService.getAllZones();
      if (response.success) {
        setZones(response.data || []);
      }
    } catch (error) {
      console.error('Fetch zones error:', error);
      alert('Бүс татахад алдаа гарлаа');
    } finally {
      setLoading(false);
    }
  };

  const fetchDistricts = async () => {
    try {
      const response = await districtAPI.getAll();
      setDistricts(response.data.data || []);
    } catch (error) {
      console.error('Fetch districts error:', error);
    }
  };

  const handleOpenModal = (zone = null) => {
    if (zone) {
      setEditingZone(zone);
      setFormData({
        zone_name: zone.zone_name,
        default_fee: zone.default_fee,
        description: zone.description || '',
        color: zone.color || '#3B82F6',
        district_ids: zone.districts?.map(d => d.id) || []
      });
    } else {
      setEditingZone(null);
      setFormData({
        zone_name: '',
        default_fee: '',
        description: '',
        color: '#3B82F6',
        district_ids: []
      });
    }
    setErrors({});
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingZone(null);
    setFormData({
      zone_name: '',
      default_fee: '',
      description: '',
      color: '#3B82F6',
      district_ids: []
    });
    setErrors({});
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleDistrictToggle = (districtId) => {
    setFormData(prev => ({
      ...prev,
      district_ids: prev.district_ids.includes(districtId)
        ? prev.district_ids.filter(id => id !== districtId)
        : [...prev.district_ids, districtId]
    }));
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.zone_name.trim()) {
      newErrors.zone_name = 'Бүсийн нэр оруулна уу';
    }
    
    if (!formData.default_fee || parseFloat(formData.default_fee) < 0) {
      newErrors.default_fee = 'Төлбөр оруулна уу';
    }
    
    if (formData.district_ids.length === 0) {
      newErrors.district_ids = 'Хамгийн багадаа 1 дүүрэг сонгоно уу';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    try {
      setSaving(true);
      
      const payload = {
        zone_name: formData.zone_name,
        default_fee: parseFloat(formData.default_fee),
        description: formData.description,
        color: formData.color,
        district_ids: formData.district_ids
      };

      if (editingZone) {
        const response = await zoneService.updateZone(editingZone.id, payload);
        if (response.success) {
          alert('✅ Бүс амжилттай шинэчлэгдлээ');
          handleCloseModal();
          fetchZones();
        }
      } else {
        const response = await zoneService.createZone(payload);
        if (response.success) {
          alert('✅ Бүс амжилттай үүслээ');
          handleCloseModal();
          fetchZones();
        }
      }
    } catch (error) {
      console.error('Save zone error:', error);
      alert(error.message || 'Алдаа гарлаа');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (zoneId, zoneName) => {
    if (!window.confirm(`"${zoneName}" бүсийг устгах уу?`)) return;

    try {
      const response = await zoneService.deleteZone(zoneId);
      if (response.success) {
        alert('✅ Бүс амжилттай устлаа');
        fetchZones();
      }
    } catch (error) {
      console.error('Delete zone error:', error);
      alert(error.message || 'Устгахад алдаа гарлаа');
    }
  };

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-2xl p-8 text-white shadow-lg">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 bg-white/20 backdrop-blur rounded-xl flex items-center justify-center">
                <span className="text-4xl">🗺️</span>
              </div>
              <div>
                <h1 className="text-3xl font-bold mb-1">Хүргэлтийн бүсчлэл</h1>
                <p className="text-purple-100">Бүс үүсгэх, засах, дүүрэг холбох</p>
              </div>
            </div>
            <button
              onClick={() => handleOpenModal()}
              className="px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:shadow-lg transition flex items-center gap-2"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              <span>Бүс нэмэх</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-purple-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Нийт бүс</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{zones.length}</p>
              </div>
              <div className="w-12 h-12 bg-purple-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">🗺️</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-green-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Идэвхтэй бүс</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">
                  {zones.filter(z => z.is_active).length}
                </p>
              </div>
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">✅</span>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-sm p-6 border-l-4 border-blue-500">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Дүүргүүд</p>
                <p className="text-3xl font-bold text-gray-900 mt-1">{districts.length}</p>
              </div>
              <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center">
                <span className="text-2xl">📍</span>
              </div>
            </div>
          </div>
        </div>

        {/* Zones Grid */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200">
          <div className="p-5 border-b border-gray-200 bg-gray-50">
            <div className="flex items-center gap-2">
              <span className="text-xl">🗺️</span>
              <h3 className="text-base font-semibold text-gray-900">Бүсүүд</h3>
            </div>
          </div>

          {loading ? (
            <div className="p-12 text-center">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-purple-600 border-t-transparent mb-4"></div>
              <p className="text-gray-600 font-medium">Уншиж байна...</p>
            </div>
          ) : zones.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <div className="text-6xl mb-4">🗺️</div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Бүс байхгүй байна</h3>
              <p className="text-sm text-gray-600 mb-4">Эхний бүсээ үүсгэж эхлээрэй</p>
              <button
                onClick={() => handleOpenModal()}
                className="px-6 py-2.5 bg-purple-600 text-white rounded-lg font-semibold hover:bg-purple-700 transition"
              >
                Бүс нэмэх
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
              {zones.map((zone) => (
                <div
                  key={zone.id}
                  className="bg-white rounded-xl border-2 border-gray-200 hover:border-purple-300 hover:shadow-md transition-all overflow-hidden"
                >
                  {/* Zone Header */}
                  <div
                    className="p-4 text-white"
                    style={{ backgroundColor: zone.color }}
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <h4 className="text-lg font-bold mb-1">{zone.zone_name}</h4>
                        <p className="text-sm opacity-90">
                          {zone.districts?.length || 0} дүүрэг
                        </p>
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => handleOpenModal(zone)}
                          className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition"
                          title="Засах"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                          </svg>
                        </button>
                        <button
                          onClick={() => handleDelete(zone.id, zone.zone_name)}
                          className="p-2 bg-white/20 hover:bg-white/30 rounded-lg transition"
                          title="Устгах"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                          </svg>
                        </button>
                      </div>
                    </div>
                  </div>

                  {/* Zone Body */}
                  <div className="p-4 space-y-3">
                    {/* Fee */}
                    <div className="flex items-center justify-between py-2 px-3 bg-gray-50 rounded-lg">
                      <span className="text-sm font-medium text-gray-600">Хүргэлтийн төлбөр:</span>
                      <span className="text-lg font-bold text-gray-900">
                        {(zone.default_fee || 0).toLocaleString()}₮
                      </span>
                    </div>

                    {/* Description */}
                    {zone.description && (
                      <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3">
                        {zone.description}
                      </div>
                    )}

                    {/* Districts */}
                    <div>
                      <p className="text-xs font-semibold text-gray-700 mb-2">Дүүргүүд:</p>
                      <div className="flex flex-wrap gap-1.5">
                        {zone.districts && zone.districts.length > 0 ? (
                          zone.districts.map((district) => (
                            <span
                              key={district.id}
                              className="inline-flex items-center px-2.5 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-medium border border-gray-200"
                            >
                              📍 {district.name}
                            </span>
                          ))
                        ) : (
                          <span className="text-xs text-gray-400 italic">Дүүрэг холбогдоогүй</span>
                        )}
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="pt-3 border-t border-gray-100">
                      <span
                        className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold ${
                          zone.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-600'
                        }`}
                      >
                        {zone.is_active ? '✅ Идэвхтэй' : '⏸️ Идэвхгүй'}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden shadow-2xl">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-purple-600 to-indigo-600 p-6 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <span className="text-3xl">🗺️</span>
                  <div>
                    <h3 className="text-2xl font-bold">
                      {editingZone ? 'Бүс засах' : 'Бүс нэмэх'}
                    </h3>
                    <p className="text-sm text-purple-100 mt-1">
                      Бүсийн мэдээлэл, төлбөр, дүүргүүд
                    </p>
                  </div>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="w-10 h-10 bg-white/20 hover:bg-white/30 rounded-lg transition flex items-center justify-center"
                >
                  <span className="text-2xl">×</span>
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 max-h-[60vh] overflow-y-auto space-y-5">
              {/* Zone Name */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Бүсийн нэр <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  name="zone_name"
                  value={formData.zone_name}
                  onChange={handleChange}
                  placeholder="Жишээ: Хотын төв (А бүс)"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 transition ${
                    errors.zone_name ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.zone_name && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>{errors.zone_name}</span>
                  </p>
                )}
              </div>

              {/* Default Fee */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Хүргэлтийн төлбөр (₮) <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="default_fee"
                  value={formData.default_fee}
                  onChange={handleChange}
                  min="0"
                  placeholder="Жишээ: 5000"
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:ring-2 focus:ring-purple-500 transition ${
                    errors.default_fee ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                />
                {errors.default_fee && (
                  <p className="text-sm text-red-600 mt-1 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>{errors.default_fee}</span>
                  </p>
                )}
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-2">
                  Тайлбар
                </label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Бүсийн тухай тайлбар (заавал биш)"
                  rows={3}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-xl focus:ring-2 focus:ring-purple-500 transition resize-none"
                />
              </div>

              {/* Color Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Өнгө сонгох
                </label>
                <div className="grid grid-cols-4 gap-3">
                  {colorOptions.map((option) => (
                    <label
                      key={option.value}
                      className={`relative flex flex-col items-center p-3 border-2 rounded-xl cursor-pointer transition ${
                        formData.color === option.value
                          ? 'border-purple-500 bg-purple-50'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="color"
                        value={option.value}
                        checked={formData.color === option.value}
                        onChange={handleChange}
                        className="sr-only"
                      />
                      <div className={`w-10 h-10 rounded-lg ${option.bg} mb-2`}></div>
                      <span className="text-xs font-medium text-gray-700">{option.label}</span>
                      {formData.color === option.value && (
                        <div className="absolute top-2 right-2 w-5 h-5 bg-purple-600 rounded-full flex items-center justify-center">
                          <svg className="w-3 h-3 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                          </svg>
                        </div>
                      )}
                    </label>
                  ))}
                </div>
              </div>

              {/* District Selection */}
              <div>
                <label className="block text-sm font-semibold text-gray-900 mb-3">
                  Дүүргүүд сонгох <span className="text-red-500">*</span>
                </label>
                <div className="bg-gray-50 rounded-xl p-4 max-h-64 overflow-y-auto border-2 border-gray-200">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {districts.map((district) => (
                      <label
                        key={district.id}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${
                          formData.district_ids.includes(district.id)
                            ? 'bg-purple-100 border-2 border-purple-300'
                            : 'bg-white border-2 border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={formData.district_ids.includes(district.id)}
                          onChange={() => handleDistrictToggle(district.id)}
                          className="w-5 h-5 text-purple-600 rounded focus:ring-2 focus:ring-purple-500"
                        />
                        <span className="text-sm font-medium text-gray-900">
                          📍 {district.name}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
                {errors.district_ids && (
                  <p className="text-sm text-red-600 mt-2 flex items-center gap-1">
                    <span>⚠️</span>
                    <span>{errors.district_ids}</span>
                  </p>
                )}
                <p className="text-xs text-gray-500 mt-2">
                  💡 {formData.district_ids.length} дүүрэг сонгогдсон
                </p>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="p-6 border-t border-gray-200 bg-gray-50 flex gap-3">
              <button
                onClick={handleCloseModal}
                disabled={saving}
                className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl font-semibold hover:bg-gray-100 transition disabled:opacity-50"
              >
                Болих
              </button>
              <button
                onClick={handleSubmit}
                disabled={saving}
                className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-600 to-indigo-600 text-white rounded-xl font-semibold hover:shadow-lg transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {saving ? (
                  <>
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Хадгалж байна...</span>
                  </>
                ) : (
                  <>
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span>{editingZone ? 'Шинэчлэх' : 'Үүсгэх'}</span>
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </CompanyLayout>
  );
};

export default DeliveryZones;